import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globals } from '../globalfunctions.js';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import roleManager, { PERMISSIONS } from '../utils/roleManager';
import { getLeadUrgency, getLeadUrgencySortValue } from '../utils/leadUrgency';
import { apiClient } from '../utils/apiClient';
import './Leads.css';
import { confirmDialog, alertDialog } from '../utils/dialogs';

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'proposal_sent', 'converted', 'lost'];
const STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  proposal_sent: 'Proposal Sent',
  converted: 'Converted',
  lost: 'Lost'
};
const SOURCE_OPTIONS = ['Website', 'Referral', 'Cold Call', 'Email Campaign', 'Trade Show', 'Other'];

const EMPTY_FORM = {
  company_name: '',
  contact_first_name: '',
  contact_last_name: '',
  email: '',
  phone: '',
  source: '',
  estimated_value: '',
  assigned_to_user_id: ''
};

function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  // "my" = assigned to me, still active (not converted/lost), sorted by
  // urgency - the default triage view. "all" = everything, for managers.
  const [viewMode, setViewMode] = useState('my');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(true);

  const canAssign = roleManager.hasPermission(PERMISSIONS.ASSIGN_LEADS);
  const canConvert = roleManager.hasPermission(PERMISSIONS.CONVERT_LEADS);
  const canDelete = roleManager.hasPermission(PERMISSIONS.DELETE_LEADS);
  const canManageSettings = roleManager.hasPermission(PERMISSIONS.MANAGE_SETTINGS);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const data = await apiClient.get('/api/leads' + qs);
      setLeads(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      navigate('/login');
      return;
    }
    fetchLeads();

    apiClient.post('/api/users/findall')
      .then((data) => setStaff(Array.isArray(data) ? data.filter((u) => (u.role_type || '').toLowerCase() !== 'customer') : []))
      .catch(() => setStaff([]));

    if (roleManager.hasPermission(PERMISSIONS.MANAGE_SETTINGS)) {
      apiClient.get('/api/mailSettings')
        .then((data) => setDigestEnabled(data.leads_digest_enabled !== false))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLeads]);

  const toggleDigest = async (checked) => {
    setDigestEnabled(checked);
    try {
      await apiClient.put('/api/mailSettings', { leads_digest_enabled: checked });
    } catch (error) {
      console.error('Error updating digest setting:', error);
    }
  };

  const deleteLead = async (id) => {
    if (!await confirmDialog('Delete this lead? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/api/leads/${id}`);
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const convertLead = async (id) => {
    if (!await confirmDialog('Convert this lead into a customer record?')) return;
    try {
      const data = await apiClient.post(`/api/leads/${id}/convert`);
      fetchLeads();
      if (await confirmDialog('Lead converted. View the new customer record now?')) {
        navigate(`/customer/${data.customer.id}`);
      }
    } catch (error) {
      console.error('Error converting lead:', error);
      alertDialog(error.message || 'Failed to convert lead');
    }
  };

  const submitNewLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post('/api/leads', {
        ...form,
        estimated_value: form.estimated_value || null,
        assigned_to_user_id: form.assigned_to_user_id || null
      });
      setShowCreateModal(false);
      setForm(EMPTY_FORM);
      fetchLeads();
    } catch (error) {
      console.error('Error creating lead:', error);
    } finally {
      setSaving(false);
    }
  };

  const stopRowClick = (fn) => (e) => { e.stopPropagation(); fn(); };

  const visibleLeads = (() => {
    let rows = leads;
    if (viewMode === 'my') {
      rows = rows.filter((l) => l.assigned_to_user_id === Globals.Id && !['converted', 'lost'].includes(l.status));
    }
    // Default order: most urgent first. Column-header sorting (built into
    // ResponsiveTable) overrides this once a user clicks a header.
    return [...rows].sort((a, b) => getLeadUrgencySortValue(a) - getLeadUrgencySortValue(b));
  })();

  const columns = [
    { key: 'company', label: 'Company', sortable: true, sortValue: (l) => l.company_name || '', render: (lead) => lead.company_name || '—' },
    {
      key: 'contact',
      label: 'Contact',
      sortable: true,
      sortValue: (l) => `${l.contact_first_name || ''} ${l.contact_last_name || ''}`.trim(),
      render: (lead) => `${lead.contact_first_name || ''} ${lead.contact_last_name || ''}`.trim() || '—'
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      sortValue: (l) => ({ high: 0, medium: 1, low: 2 }[l.priority] ?? 1),
      render: (lead) => <span className={`lead-priority-pill lead-priority-${lead.priority}`}>{lead.priority}</span>
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      sortValue: (l) => STATUS_LABELS[l.status] || '',
      render: (lead) => <span className={`lead-status-pill lead-status-${lead.status}`}>{STATUS_LABELS[lead.status] || lead.status}</span>
    },
    {
      key: 'followup',
      label: 'Follow-Up',
      sortable: true,
      sortValue: (l) => getLeadUrgencySortValue(l),
      render: (lead) => {
        const urgency = getLeadUrgency(lead);
        return (
          <span className={`lead-urgency-pill lead-urgency-${urgency.key}`}>
            {lead.next_follow_up_date ? `${lead.next_follow_up_date} · ${urgency.label}` : urgency.label}
          </span>
        );
      }
    },
    {
      key: 'assigned',
      label: 'Assigned To',
      sortable: true,
      sortValue: (l) => l.assignedTo ? `${l.assignedTo.first_name} ${l.assignedTo.last_name}` : '',
      render: (lead) => lead.assignedTo ? `${lead.assignedTo.first_name} ${lead.assignedTo.last_name}` : 'Unassigned'
    },
    {
      key: 'estimated_value',
      label: 'Est. Value',
      sortable: true,
      sortValue: (l) => parseFloat(l.estimated_value) || 0,
      render: (lead) => lead.estimated_value ? `£${parseFloat(lead.estimated_value).toFixed(2)}` : '—'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (lead) => (
        <div className="lead-actions">
          {canConvert && lead.status !== 'converted' && (
            <Button variant="secondary" onClick={stopRowClick(() => convertLead(lead.id))}>Convert</Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={stopRowClick(() => deleteLead(lead.id))}>Delete</Button>
          )}
        </div>
      )
    }
  ];

  return (
    <PageShell>
      <div className="leads-header">
        <h1>Leads</h1>
        <div className="leads-header-controls">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          {roleManager.hasPermission(PERMISSIONS.CREATE_LEADS) && (
            <Button onClick={() => setShowCreateModal(true)}>New Lead</Button>
          )}
        </div>
      </div>

      {canManageSettings && (
        <label className="leads-digest-toggle">
          <input type="checkbox" checked={digestEnabled} onChange={(e) => toggleDigest(e.target.checked)} />
          Send daily follow-up digest emails
        </label>
      )}

      <div className="leads-view-toggle">
        <button type="button" className={`lead-view-toggle-btn ${viewMode === 'my' ? 'active' : ''}`} onClick={() => setViewMode('my')}>
          My Leads
        </button>
        <button type="button" className={`lead-view-toggle-btn ${viewMode === 'all' ? 'active' : ''}`} onClick={() => setViewMode('all')}>
          All Leads
        </button>
      </div>
      {viewMode === 'my' && (
        <p className="leads-overview-hint">
          Leads assigned to you that are still active, most urgent first. Converted and lost leads aren't shown here.
        </p>
      )}

      {loading ? (
        <p>Loading leads...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={visibleLeads}
          onRowClick={(lead) => navigate(`/leads/${lead.id}`)}
          emptyMessage={viewMode === 'my' ? "Nothing needs your attention right now." : 'No leads yet. Add one to get started.'}
        />
      )}

      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="New Lead"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button type="submit" form="new-lead-form" disabled={saving}>{saving ? 'Saving...' : 'Save Lead'}</Button>
          </>
        )}
      >
        <form id="new-lead-form" onSubmit={submitNewLead}>
          <FormField id="lead-company" label="Company Name">
            <input id="lead-company" type="text" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </FormField>
          <FormField id="lead-first" label="Contact First Name">
            <input id="lead-first" type="text" value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} />
          </FormField>
          <FormField id="lead-last" label="Contact Last Name">
            <input id="lead-last" type="text" value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} />
          </FormField>
          <FormField id="lead-email" label="Email">
            <input id="lead-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </FormField>
          <FormField id="lead-phone" label="Phone">
            <input id="lead-phone" type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </FormField>
          <FormField id="lead-source" label="Source">
            <select id="lead-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option value="">Select a source...</option>
              {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </FormField>
          <FormField id="lead-value" label="Estimated Value (£)">
            <input id="lead-value" type="number" step="0.01" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} />
          </FormField>
          {canAssign && (
            <FormField id="lead-assigned" label="Assign To">
              <select id="lead-assigned" value={form.assigned_to_user_id} onChange={(e) => setForm({ ...form, assigned_to_user_id: e.target.value })}>
                <option value="">Unassigned</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                ))}
              </select>
            </FormField>
          )}
        </form>
      </Modal>
    </PageShell>
  );
}

export default Leads;
